/**
 * Préférences d'affichage regroupées — thème, densité, identifiants techniques.
 *
 * POURQUOI LES REGROUPER
 * ----------------------
 * Ces trois bascules occupaient chacune une place permanente dans la barre supérieure, aux
 * côtés de la recherche, de la création, du sélecteur de programme, de celui de projet et du
 * bloc d'identité — treize éléments en tout. Le retour d'usage était sans appel : « la barre
 * horizontale du haut est trop encombrée ».
 *
 * Ce sont des préférences qu'on règle une fois et qu'on ne retouche presque jamais. Les
 * laisser en permanence coûtait de la place à ce qu'on utilise à chaque minute — le contexte
 * de travail — au profit de ce qu'on utilise une fois par trimestre.
 *
 * Elles restent à un clic, et le menu affiche l'état courant de chacune : regrouper ne doit
 * pas obliger à ouvrir pour savoir où l'on en est.
 */
import { useState, type JSX } from 'react'

import { DropdownMenuCheckboxItem } from '@/components/ui/dropdown-menu'
import { ThemeToggle } from '@/components/ThemeToggle'
import { getDensity, setDensity, type Density } from '@/lib/density'
import { useTechnical } from '@/lib/technical'
import { useT } from '@/i18n/language'

/**
 * Le CONTENU des préférences, monté dans le menu utilisateur (`DropdownMenuContent`,
 * `role="menu"`).
 *
 * v030-menu-utilisateur-aria-hidden-focus-a-l-ouverture : la densité et le mode technique
 * sont des bascules booléennes — `DropdownMenuCheckboxItem` (role="menuitemcheckbox") est
 * le primitive Radix prévu pour exactement ce cas, un enfant valide de `role="menu"`. Les
 * anciens `<DensityToggle/>`/`<TechnicalToggle/>` (de simples `<button>`) restaient des
 * enfants non conformes une fois placés directement sous `role="menu"`
 * (aria-required-children, critical) ; ils gardent leur fichier et leur test unitaire
 * propre (usage standalone possible), mais ce menu appelle directement `getDensity`/
 * `setDensity`/`useTechnical` — le même état, sans le bouton autonome intermédiaire.
 */
export function DisplayMenuContent(): JSX.Element {
  const { t } = useT()
  const [density, setDensityState] = useState<Density>(getDensity())
  const compact = density === 'compact'
  const { technical, setTechnical } = useTechnical()

  const toggleDensity = (): void => {
    const suivante: Density = compact ? 'comfortable' : 'compact'
    setDensity(suivante)
    setDensityState(suivante)
  }

  return (
    <div className="flex flex-col">
      <ThemeToggle />
      <DropdownMenuCheckboxItem
        checked={compact}
        onCheckedChange={toggleDensity}
        data-testid="density-toggle"
        onSelect={(e) => e.preventDefault()}
      >
        {t('display.density', 'Densité compacte')}
      </DropdownMenuCheckboxItem>
      <DropdownMenuCheckboxItem
        checked={technical}
        onCheckedChange={setTechnical}
        data-testid="technical-toggle"
        onSelect={(e) => e.preventDefault()}
      >
        {t('display.technical', 'Identifiants techniques')}
      </DropdownMenuCheckboxItem>
    </div>
  )
}
